import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, ViewEncapsulation } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DxTooltipModule } from 'devextreme-angular';
import { getStatusIconClass, getStatusIndicatorClass } from './status-badge.utils';

@Component({
    selector: 'app-status-badge',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoPipe, DxTooltipModule],
    templateUrl: './status-badge.component.html',
    styleUrl: './status-badge.component.scss',
    host: {
        '[class]': '"badge status app-status-badge " + hostClasses() + iconOnly()',
        '[style.height.px]': 'height()',
        '[style.min-height.px]': 'height()',
    },
})
export class StatusBadgeComponent {
    /** Domain enum value (e.g. `Draft`, `InProgress`, `Canceled`). */
    readonly status = input.required<string | null | undefined>();

    /** Plain label; overrides transloco when set. */
    readonly label = input<string | null>(null);

    /** Full transloco key for the label. */
    readonly translationKey = input<string | null>(null);

    /** Builds `translationKey` as `{prefix}{status}` when `translationKey` is not set. */
    readonly translationPrefix = input<string | null>(null);

    readonly showIcon = input(true);
    readonly showText = input(true);

    /** Overrides the default icon for this status. */
    readonly icon = input<string | null>(null);

    /** Plain hover tooltip text; overrides `tooltipTranslationKey` and the label fallback when set. */
    readonly tooltip = input<string | null>(null);

    /** Full transloco key for the hover tooltip; falls back to the badge label when not set. */
    readonly tooltipTranslationKey = input<string | null>(null);

    /** Hover tooltip anchor: the badge host element, so the whole badge is hoverable. */
    protected readonly tooltipTarget = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    /** Icon size in px; falls back to the CSS default when not set. */
    readonly iconSize = input<number | null>(null);

    /** Badge height in px; overrides the default `min-height` when set. */
    readonly height = input<number | null>(null);

    readonly iconClass = computed(() => {
        if (!this.showIcon()) {
            return null;
        }

        return getStatusIconClass(this.status(), this.icon());
    });

    readonly hostClasses = computed(() => getStatusIndicatorClass(this.status()));

    readonly iconOnly = computed(() => {
        if (this.showIcon() && !this.showText()) {
            return ' icon-only';
        }

        return '';
    });

    readonly resolvedTranslationKey = computed(() => {
        if (this.translationKey()) {
            return this.translationKey();
        }

        const prefix = this.translationPrefix();
        const status = this.status();

        if (prefix && status) {
            return `${prefix}${status}`;
        }

        return null;
    });

    /** Only render the tooltip when there is actually something to show. */
    readonly hasTooltip = computed(() => !!(this.tooltip() || this.tooltipTranslationKey() || this.label() || this.resolvedTranslationKey()));
}
